import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { colors } from '@lusha-ui/theme';
import ModalStyled from '@components/modal/ModalStyled';
import { useMST } from '@hooks/MSTHook';
import { Text, Link } from '@lusha-ui/text';
import { Sparkle2Icon, ExternalLinkIcon } from '@lusha-ui/icons';
import { Tab, Tabs } from '@lusha-ui/tabs';
import { Loader } from '@lusha-ui/core';
import { CircleStar } from '@lusha/icons';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PlanCard from './components/PlanCard';
import { BILLING_CYCLES, HEADLINE_CONFIG } from './constants';
import { useAnalytics, AnalyticsContextProvider } from '@lusha-engage/analytics';
const UpgradePlanModal = observer(() => {
    const analytics = useAnalytics();
    const { pricing, totalCredits, userPlanTotalCredits } = useMST(store => ({
        pricing: store.pricing,
        totalCredits: store.totalCredits,
        userPlanTotalCredits: store.userPlanTotalCredits,
    }));
    const [billingCycle, setBillingCycle] = useState(BILLING_CYCLES.MONTHLY);
    const [selectedCredits, setSelectedCredits] = useState(0);
    const [selectedSeats, setSelectedSeats] = useState(0);
    const isVisible = pricing.isModalVisible;
    const isOutOfCreditsFlow = pricing.openSource === 'credit_exhaustion';
    const handleBillingCycleUpdate = (cycle) => {
        setBillingCycle(cycle);
        setSelectedCredits(0);
        setSelectedSeats(0);
        analytics.track({
            eventType: 'pricing_toggle_clicked',
            event_value: cycle,
            event_additional_info: {
                selected_plan: 'professional',
                current_credit_balance: totalCredits,
                plugin_component: 'free_to_paid_upgrade_pop_up',
                plugin_click_source: pricing.openSource,
            }
        });
    };
    const headlineConfig = useMemo(() => {
        if (totalCredits === 0) {
            return HEADLINE_CONFIG.outOfCredits;
        }
        if (totalCredits >= 1 && totalCredits <= 15) {
            return HEADLINE_CONFIG.almostOut;
        }
        return HEADLINE_CONFIG.healthy;
    }, [totalCredits]);
    const resetToDefaults = useCallback(() => {
        setBillingCycle(BILLING_CYCLES.MONTHLY);
        setSelectedCredits(0);
        setSelectedSeats(0);
    }, []);
    function handleClose() {
        pricing.closeUpgradeModal();
        resetToDefaults();
    }
    function handleCompareClick() {
        analytics.track({
            eventType: 'upgrade_compare_plans_clicked',
            event_additional_info: {
                selected_plan: 'professional',
                current_credit_balance: totalCredits,
                plugin_component: 'free_to_paid_upgrade_pop_up',
                plugin_click_source: pricing.openSource,
            }
        });
    }
    useEffect(() => {
        if (isVisible) {
            resetToDefaults();
            analytics.track({
                eventType: 'pricing_exposed',
                event_page_state_str: 'upgrade_pop_up',
                event_trigger_type: pricing.openSource,
                event_additional_info: {
                    plugin_click_source: pricing.openSource,
                    selected_plan: 'professional',
                    current_credit_balance: totalCredits,
                    plugin_component: 'free_to_paid_upgrade_pop_up',
                },
            });
        }
    }, [isVisible]);
    return (_jsx(ModalStyled, { isVisible: isVisible, onClose: handleClose, name: "upgrade-plan", wrapperPadding: 16, children: _jsx(AnalyticsContextProvider, { event_additional_info: {
                selected_plan: 'professional',
                current_credit_balance: totalCredits,
                plugin_component: 'free_to_paid_upgrade_pop_up',
                plugin_click_source: pricing.openSource,
            }, children: _jsxs("div", { css: css `
            display: flex;
            flex-direction: column;
          `, children: [_jsxs("div", { css: css `
              display: flex;
              flex-direction: column;
              margin-bottom: 16px;
            `, children: [_jsx("div", { css: css `
                display: flex;
                align-items: center;
                margin-bottom: 4px;
              `, children: isOutOfCreditsFlow
                                    ? _jsx(Text, { variant: "h3-b", children: "You\u2019ve run out of credits" })
                                    : (_jsxs(_Fragment, { children: [_jsx(Sparkle2Icon, { css: css `
                        margin-right: 6px;
                      `, color: colors.lightScaleYellow, size: "normal" }), _jsx(Text, { variant: "h3-b", children: headlineConfig.headline })] })) }), _jsx(Text, { variant: "body-2-r", children: isOutOfCreditsFlow
                                    ? 'Upgrade your plan to get more credits and unlock advanced capabilities'
                                    : headlineConfig.subtext })] }), _jsxs("div", { css: css `
              padding: 8px;
              background: ${colors.lightGrey};
              border-radius: 4px;
              margin-bottom: 24px;
            `, children: [_jsx(CircleStar, { variant: "solid", color: "secondary" }), _jsxs(Text, { variant: "body-2-r", css: css `
                margin-left: 4px;
              `, children: ["Current plan: Free \u00B7 ", userPlanTotalCredits || 50, " credits/mo."] })] }), _jsx("div", { css: css `
              display: flex;
              justify-content: center;
            `, children: _jsxs(Tabs, { selectedTab: billingCycle, onChange: handleBillingCycleUpdate, size: "small", css: css `
                margin-bottom: 16px;
              `, children: [_jsx(Tab, { value: BILLING_CYCLES.ANNUAL, active: billingCycle === BILLING_CYCLES.ANNUAL, label: "Annual (25% off)" }, `upgrade-options-tab-annual`), _jsx(Tab, { value: BILLING_CYCLES.MONTHLY, active: billingCycle === BILLING_CYCLES.MONTHLY, label: "Monthly" }, `upgrade-options-tab-monthly`)] }) }), pricing.isLoaded && (_jsxs("div", { children: [_jsx(PlanCard, { selectedCredits: selectedCredits, selectedSeats: selectedSeats, billingCycle: billingCycle, onCreditsChange: setSelectedCredits, onSeatsChange: setSelectedSeats, handleClose: handleClose }), _jsx("div", { css: css `
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  margin-top: 16px;
                `, children: _jsxs(Text, { variant: "body-2-r", color: colors.text_and_icons.secondary.DEFAULT, children: ["Need more?", ' ', _jsxs(Link, { suffixElement: _jsx(ExternalLinkIcon, {}), target: "_blank", onClick: handleCompareClick, href: `${envConfig.DASHBOARD_URL}/pricing/?dashboard`, children: ["Compare all plans", ' '] })] }) })] })), pricing.isLoading && !pricing.isLoaded && (_jsx("div", { css: css `
                display: flex;
                align-items: center;
                justify-content: center;
                min-height: 300px;
              `, children: _jsx(Loader, { css: css `
                  margin-right: 8px;
                `, size: 50, backgroundColor: 'white' }) }))] }) }) }));
});
export default UpgradePlanModal;
